/**
 * Marketplace API Service
 *
 * API methods for marketplace operations including discovery, installation,
 * and partner publishing.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse } from '../types/api.types';
import type {
  MarketplaceListing,
  MarketplaceSearchQuery,
  MarketplaceInstallDTO,
  MarketplaceUpgradeDTO,
  MarketplaceListingCreateDTO,
  MarketplaceListingUpdateDTO,
  MarketplaceVersionCreateDTO,
  CheckUpdatesResponse,
  MarketplaceCategory,
  MarketplaceListingsResponse,
} from '../types/marketplace.types';
import type { ApplicationSanitized } from '../types/application.types';

// ============================================================================
// PUBLIC MARKETPLACE SERVICE (Discovery)
// ============================================================================

export const marketplaceService = {
  /**
   * List all published marketplace listings with pagination
   */
  listListings: async (query?: MarketplaceSearchQuery): Promise<MarketplaceListingsResponse> => {
    const response = await apiClient.get<ApiCollectionResponse<MarketplaceListing>>('/marketplace/listings', {
      params: query,
    });
    return {
      items: response.data.data,
      hasMore: response.data.pagination.hasMore,
      cursor: response.data.pagination.cursor,
    };
  },

  /**
   * Get a single marketplace listing by ID
   */
  getListing: async (listingId: string): Promise<MarketplaceListing> => {
    const response = await apiClient.get<ApiSuccessResponse<MarketplaceListing>>(`/marketplace/listings/${listingId}`);
    return response.data.data;
  },

  /**
   * Get listings by category with pagination
   */
  getListingsByCategory: async (category: MarketplaceCategory, query?: MarketplaceSearchQuery): Promise<MarketplaceListingsResponse> => {
    const response = await apiClient.get<ApiCollectionResponse<MarketplaceListing>>('/marketplace/listings', {
      params: { ...query, category },
    });
    return {
      items: response.data.data,
      hasMore: response.data.pagination.hasMore,
      cursor: response.data.pagination.cursor,
    };
  },

  /**
   * Get all marketplace categories
   */
  getCategories: async (): Promise<MarketplaceCategory[]> => {
    const response = await apiClient.get<ApiSuccessResponse<{ categories: MarketplaceCategory[] }>>('/marketplace/categories');
    return response.data.data.categories;
  },

  /**
   * Get a specific version of a listing
   */
  getListingVersion: async (listingId: string, version: string): Promise<MarketplaceListing & { requestedVersion: string }> => {
    const response = await apiClient.get<ApiSuccessResponse<MarketplaceListing & { requestedVersion: string }>>(`/marketplace/listings/${listingId}/versions/${version}`);
    return response.data.data;
  },
};

// ============================================================================
// TENANT MARKETPLACE SERVICE (Install/Upgrade)
// ============================================================================

export const tenantMarketplaceService = {
  /**
   * Install a marketplace application
   */
  installApp: async (tenantId: string, data: MarketplaceInstallDTO): Promise<ApplicationSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<ApplicationSanitized>>(`/tenants/${tenantId}/marketplace/install`, data);
    return response.data.data;
  },

  /**
   * List installed marketplace applications (includes update availability info)
   */
  listInstalledApps: async (tenantId: string): Promise<(ApplicationSanitized & { updateAvailable?: boolean; latestVersion?: string })[]> => {
    const response = await apiClient.get<ApiCollectionResponse<ApplicationSanitized & { updateAvailable?: boolean; latestVersion?: string }>>(`/tenants/${tenantId}/marketplace/installed`);
    return response.data.data;
  },

  /**
   * Check for available updates for a specific application
   */
  checkForUpdates: async (tenantId: string, applicationId: string): Promise<CheckUpdatesResponse> => {
    const response = await apiClient.get<ApiSuccessResponse<CheckUpdatesResponse>>(`/tenants/${tenantId}/marketplace/installed/${applicationId}/updates`);
    return response.data.data;
  },

  /**
   * Upgrade an installed marketplace application
   */
  upgradeApp: async (tenantId: string, applicationId: string, data?: MarketplaceUpgradeDTO): Promise<ApplicationSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<ApplicationSanitized>>(`/tenants/${tenantId}/marketplace/installed/${applicationId}/upgrade`, data || {});
    return response.data.data;
  },
};

// ============================================================================
// PARTNER MARKETPLACE SERVICE (Publishing)
// ============================================================================

export const partnerMarketplaceService = {
  /**
   * Check if tenant is a marketplace partner
   */
  getPartnerStatus: async (tenantId: string): Promise<{ isPartner: boolean }> => {
    const response = await apiClient.get<ApiSuccessResponse<{ isPartner: boolean }>>(`/tenants/${tenantId}/marketplace/partner-status`);
    return response.data.data;
  },

  /**
   * Publish an application to the marketplace
   */
  publishApp: async (tenantId: string, data: MarketplaceListingCreateDTO): Promise<MarketplaceListing> => {
    const response = await apiClient.post<ApiSuccessResponse<MarketplaceListing>>(`/tenants/${tenantId}/marketplace/publish`, data);
    return response.data.data;
  },

  /**
   * List all listings published by this tenant
   */
  listPublishedApps: async (tenantId: string): Promise<MarketplaceListing[]> => {
    const response = await apiClient.get<ApiCollectionResponse<MarketplaceListing>>(`/tenants/${tenantId}/marketplace/published`);
    return response.data.data;
  },

  /**
   * Update a marketplace listing
   */
  updateListing: async (tenantId: string, listingId: string, data: MarketplaceListingUpdateDTO): Promise<MarketplaceListing> => {
    const response = await apiClient.put<ApiSuccessResponse<MarketplaceListing>>(`/tenants/${tenantId}/marketplace/listings/${listingId}`, data);
    return response.data.data;
  },

  /**
   * Add a new version to a listing
   */
  addVersion: async (tenantId: string, listingId: string, data: MarketplaceVersionCreateDTO): Promise<MarketplaceListing> => {
    const response = await apiClient.post<ApiSuccessResponse<MarketplaceListing>>(`/tenants/${tenantId}/marketplace/listings/${listingId}/versions`, data);
    return response.data.data;
  },

  /**
   * Unpublish a marketplace listing
   */
  unpublishListing: async (tenantId: string, listingId: string): Promise<MarketplaceListing> => {
    const response = await apiClient.delete<ApiSuccessResponse<MarketplaceListing>>(`/tenants/${tenantId}/marketplace/listings/${listingId}`);
    return response.data.data;
  },

  /**
   * Get a listing for the publisher (including unpublished)
   */
  getListing: async (tenantId: string, listingId: string): Promise<MarketplaceListing> => {
    const response = await apiClient.get<ApiSuccessResponse<MarketplaceListing>>(
      `/tenants/${tenantId}/marketplace/listings/${listingId}`
    );
    return response.data.data;
  },

  /**
   * Republish an unpublished marketplace listing
   */
  republishListing: async (tenantId: string, listingId: string): Promise<MarketplaceListing> => {
    const response = await apiClient.post<ApiSuccessResponse<MarketplaceListing>>(
      `/tenants/${tenantId}/marketplace/listings/${listingId}/republish`
    );
    return response.data.data;
  },
};

export default {
  ...marketplaceService,
  tenant: tenantMarketplaceService,
  partner: partnerMarketplaceService,
};
